import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Users, Award, DollarSign, HeadphonesIcon, Rocket, Target, CheckCircle2, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Careers = () => {
  const benefits = [
    {
      icon: DollarSign,
      title: '60% Commission Splits',
      description: 'Industry-leading commission structure that rewards your hard work. Keep 60% of what you earn - no hidden fees, no catches.'
    },
    {
      icon: HeadphonesIcon,
      title: 'Full Back-End Support',
      description: 'Dedicated administrative team handling contracts, compliance, payroll, and all the paperwork so you can focus on billing.'
    },
    {
      icon: Users,
      title: 'Complete Front-End Support',
      description: 'Professional marketing, brand management, CRM systems, and lead generation support to maximize your success.'
    },
    {
      icon: Rocket,
      title: 'Proven Systems & Training',
      description: 'Access our battle-tested recruitment frameworks, ongoing training, and mentorship from top billers in the industry.'
    },
    {
      icon: Award,
      title: 'Established Brand & Network',
      description: 'Leverage Growth Talent Group\'s reputation and extensive network of clients across Sydney and Australia.'
    },
    {
      icon: Target,
      title: 'Exclusive Client Base',
      description: 'Work with premium property and real estate clients actively seeking top talent. We\'ve done the groundwork.'
    }
  ];

  const idealCandidate = [
    'Minimum 2 years recruitment experience (property/real estate preferred)',
    'Proven track record of consistent billing',
    'Self-motivated with entrepreneurial mindset',
    'Strong relationship-building skills',
    'Hunter mentality with a passion for sales',
    'Licensed or willing to obtain relevant certifications'
  ];

  const whatYouGet = [
    'Desk fee model with 60% commission split',
    'Modern office space in Sydney CBD (or work remotely)',
    'Access to premium job boards and databases',
    'Professional CRM and recruitment technology',
    'Marketing materials and personal branding support',
    'Monthly team meetings and training sessions',
    'Flexible working arrangements',
    'Uncapped earning potential'
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="relative py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-400 via-brand-500 to-brand-600 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center text-white">
            <div className="inline-block mb-6 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full">
              <p className="text-white font-bold text-lg">🚀 Now Hiring Experienced Recruiters</p>
            </div>
            <h1 className="text-5xl md:text-7xl font-black mb-8 tracking-tight leading-tight">
              Build Your Recruitment<br />Empire with Us
            </h1>
            <p className="text-xl md:text-2xl mb-4 leading-relaxed max-w-4xl mx-auto text-white/95">
              Join Sydney's fastest-growing property recruitment agency. Keep 60% of what you earn with full back and front-end support.
            </p>
            <p className="text-2xl md:text-3xl font-black mb-12 text-white">
              No BS. Just Results.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-brand-400 hover:bg-gray-100 font-bold text-lg px-12 py-8 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105">
                  Apply Now
                  <ArrowRight className="ml-2" size={24} />
                </Button>
              </Link>
              <a href="#why-join">
                <Button size="lg" variant="outline" className="border-3 border-white text-white hover:bg-white hover:text-brand-400 font-bold text-lg px-12 py-8 bg-transparent backdrop-blur-sm shadow-2xl transition-all duration-300 hover:scale-105">
                  Learn More
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-6xl font-black text-brand-400 mb-3">60%</div>
              <p className="text-gray-700 text-lg">Commission Split</p>
            </div>
            <div>
              <div className="text-6xl font-black text-brand-400 mb-3">$500K+</div>
              <p className="text-gray-700 text-lg">Average Top Biller Earnings</p>
            </div>
            <div>
              <div className="text-6xl font-black text-brand-400 mb-3">150+</div>
              <p className="text-gray-700 text-lg">Active Client Partners</p>
            </div>
            <div>
              <div className="text-6xl font-black text-brand-400 mb-3">100%</div>
              <p className="text-gray-700 text-lg">Back-End Support</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section id="why-join" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6 tracking-tight leading-tight">
              Why Top Recruiters <span className="text-brand-400">Choose Us</span>
            </h2>
            <p className="text-gray-600 text-xl max-w-3xl mx-auto leading-relaxed">
              We've built the ultimate platform for ambitious recruiters who want to maximize their earnings while minimizing admin headaches.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit) => (
              <Card key={benefit.title} className="bg-white border-2 border-gray-200 hover:border-brand-400 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-10">
                  <div className="w-20 h-20 bg-brand-400/10 rounded-2xl flex items-center justify-center mb-6">
                    <benefit.icon size={40} className="text-brand-400" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-4 tracking-tight">{benefit.title}</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-8 tracking-tight leading-tight">
                The Complete <span className="text-brand-400">Package</span>
              </h2>
              <p className="text-gray-600 text-xl mb-10 leading-relaxed">
                Everything you need to succeed as an independent recruiter, without the overhead of running your own business.
              </p>
              <ul className="space-y-5">
                {whatYouGet.map((item) => (
                  <li key={item} className="flex items-start text-lg text-gray-700">
                    <CheckCircle2 size={28} className="text-brand-400 mr-4 mt-0.5 flex-shrink-0" />
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <Card className="bg-gradient-to-br from-brand-400 to-brand-600 border-none shadow-2xl">
                <CardContent className="p-12 text-white">
                  <Star size={48} className="mb-6" />
                  <h3 className="text-3xl font-black mb-6 leading-tight">Earnings Potential</h3>
                  <div className="space-y-6 text-lg">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                      <p className="text-sm mb-2 text-white/80">Conservative Biller</p>
                      <p className="text-3xl font-black">$150K - $250K</p>
                      <p className="text-sm mt-2 text-white/80">Annual earnings</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                      <p className="text-sm mb-2 text-white/80">High Performer</p>
                      <p className="text-3xl font-black">$300K - $500K</p>
                      <p className="text-sm mt-2 text-white/80">Annual earnings</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 border-2 border-white/30">
                      <p className="text-sm mb-2 text-white/90">Top Biller</p>
                      <p className="text-4xl font-black">$500K+</p>
                      <p className="text-sm mt-2 text-white/90">Sky's the limit</p>
                    </div>
                  </div>
                  <p className="mt-8 text-white/90 italic">
                    *Based on 60% commission split. Your earnings depend on your hustle.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal Candidate Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 tracking-tight leading-tight">
              Are You the <span className="text-brand-400">Right Fit?</span>
            </h2>
            <p className="text-gray-600 text-xl leading-relaxed">
              We're looking for proven recruiters who are ready to take their earnings to the next level.
            </p>
          </div>

          <Card className="bg-white border-2 border-gray-200 shadow-xl">
            <CardContent className="p-12">
              <h3 className="text-2xl font-black text-gray-900 mb-8 tracking-tight">Ideal Candidate Profile:</h3>
              <ul className="space-y-5">
                {idealCandidate.map((item, index) => (
                  <li key={item} className="flex items-start text-lg text-gray-700">
                    <div className="w-8 h-8 bg-brand-400 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-black">
                      {index + 1}
                    </div>
                    <span className="leading-relaxed pt-0.5">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-12 pt-8 border-t border-gray-200">
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  <strong>Not ticking all the boxes?</strong> If you're a hustler with the right attitude and proven sales track record, we still want to hear from you.
                </p>
                <Link to="/contact">
                  <Button size="lg" className="bg-brand-400 hover:bg-brand-500 text-white font-bold text-lg px-10 py-7 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 w-full sm:w-auto">
                    Start Your Application
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-8 tracking-tight leading-tight">
            Ready to Take Control of Your Earnings?
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed">
            Join a team that values your expertise and rewards your results. Let's have a confidential conversation about your future.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-brand-400 hover:bg-brand-500 text-white font-bold text-lg px-12 py-8 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105">
                Apply Now
                <ArrowRight className="ml-2" size={24} />
              </Button>
            </Link>
            <a href="tel:1300474684">
              <Button size="lg" variant="outline" className="border-3 border-brand-400 text-brand-400 hover:bg-brand-400 hover:text-white font-bold text-lg px-12 py-8 shadow-2xl transition-all duration-300 hover:scale-105">
                Call 1300 GROWTH
              </Button>
            </a>
          </div>
          <p className="mt-8 text-gray-500 text-sm">
            All conversations are strictly confidential. We understand you might be currently employed.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Careers;
